import ApiClient from '../apiClient';

interface TransactionFilters {
  page?: number;
  page_size?: number;
  id?: string;
  of_id?: number;
  of_company_id?: number;
  company_id?: string;
  model_id?: string;
  of_model_id?: number;
  created_at_start?: string;
  created_at_end?: string;
  status?: string;
}

interface SubscriberFilters {
  page?: number;
  page_size?: number;
  id?: string;
  of_company_id?: number;
  company_id?: string;
  model_id?: string;
  of_id?: number;
  of_model_id?: number;
  name?: string;
  has_gte_three_messages?: boolean;
  one_transaction?: boolean;
  two_transaction?: boolean;
  five_transaction?: boolean;
  amount_more_500?: boolean;
  paid?: boolean;
  sort_by?: string;
}

interface StatisticsFilters {
  page?: number;
  page_size?: number;
  sort_by?: string;
  start_date?: string;
  end_date?: string;
  of_model_id?: number | null;
  company_id?: number | null;
  grouping?: string;
}

interface StatisticsResponse {
  page: number;
  page_size: number;
  count: number;
  objects: Array<{
    date: string;
    count?: number;
    amount?: string;
  }>;
}

const clientStatisticService = {
  getTransactions: (filters: TransactionFilters = {}) => {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.page_size) queryParams.append('page_size', filters.page_size.toString());

    // Add filter parameters if they exist
    if (filters.id) queryParams.append('id', filters.id);
    if (filters.of_id) queryParams.append('of_id', filters.of_id.toString());
    if (filters.of_company_id)
      queryParams.append('of_company_id', filters.of_company_id.toString());
    if (filters.company_id) queryParams.append('company_id', filters.company_id);
    if (filters.model_id) queryParams.append('model_id', filters.model_id);
    if (filters.of_model_id) queryParams.append('of_model_id', filters.of_model_id.toString());
    if (filters.created_at_start) queryParams.append('created_at_start', filters.created_at_start);
    if (filters.created_at_end) queryParams.append('created_at_end', filters.created_at_end);
    if (filters.status) queryParams.append('status', filters.status);

    return ApiClient.get(`/client/transactions?${queryParams.toString()}`);
  },
  getSubscribers: (filters: SubscriberFilters = {}) => {
    const queryParams = new URLSearchParams();

    // Add pagination parameters
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.page_size) queryParams.append('page_size', filters.page_size.toString());

    // Add filter parameters if they exist
    if (filters.id) queryParams.append('id', filters.id);
    if (filters.of_company_id)
      queryParams.append('of_company_id', filters.of_company_id.toString());
    if (filters.sort_by) queryParams.append('sort_by', filters.sort_by.toString());
    if (filters.company_id) queryParams.append('company_id', filters.company_id);
    if (filters.model_id) queryParams.append('model_id', filters.model_id);
    if (filters.of_id) queryParams.append('of_id', filters.of_id.toString());
    if (filters.of_model_id) queryParams.append('of_model_id', filters.of_model_id.toString());
    if (filters.name) queryParams.append('name', filters.name);
    if (filters.has_gte_three_messages !== undefined)
      queryParams.append('has_gte_three_messages', filters.has_gte_three_messages.toString());
    if (filters.one_transaction !== undefined)
      queryParams.append('one_transaction', filters.one_transaction.toString());
    if (filters.two_transaction !== undefined)
      queryParams.append('two_transaction', filters.two_transaction.toString());
    if (filters.five_transaction !== undefined)
      queryParams.append('five_transaction', filters.five_transaction.toString());
    if (filters.amount_more_500 !== undefined)
      queryParams.append('amount_more_500', filters.amount_more_500.toString());
    if (filters.paid !== undefined) queryParams.append('paid', filters.paid.toString());

    return ApiClient.get(`/client/subscribers?${queryParams.toString()}`);
  },
  getStatsTransactionsByDay: () => ApiClient.get('/client/transactions/stats/by_days'),
  getStatsTransactionsByWeek: () => ApiClient.get('/client/transactions/stats/by_weeks'),
  getStatsTransactionsByMonth: () => ApiClient.get('/client/transactions/stats/by_month'),
  getStatsSubscribersByDay: () => ApiClient.get('/client/subscribers/stats/by_days'),
  getStatsSubscribersByWeek: () => ApiClient.get('/client/subscribers/stats/by_weeks'),
  getStatsSubscribersByMonth: () => ApiClient.get('/client/subscribers/stats/by_month'),
  getTransactionsOwn: () => ApiClient.get('/client/transactionsall_amount'),
  // New statistics API methods with filters
  getSubscribersStats: (filters: StatisticsFilters = {}): Promise<StatisticsResponse> => {
    const queryParams = new URLSearchParams();

    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.page_size) queryParams.append('page_size', filters.page_size.toString());
    if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.of_model_id !== undefined)
      queryParams.append('of_model_id', filters.of_model_id?.toString() || '');
    if (filters.company_id !== undefined)
      queryParams.append('company_id', filters.company_id?.toString() || '');

    // Choose endpoint based on grouping
    let endpoint = '/client/subscribers/stats/by_days';
    if (filters.grouping === 'week') {
      endpoint = '/client/subscribers/stats/by_weeks';
    } else if (filters.grouping === 'month') {
      endpoint = '/client/subscribers/stats/by_month';
    }

    return ApiClient.get(`${endpoint}?${queryParams.toString()}`);
  },

  getTransactionsStats: (filters: StatisticsFilters = {}): Promise<StatisticsResponse> => {
    const queryParams = new URLSearchParams();

    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.page_size) queryParams.append('page_size', filters.page_size.toString());
    if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
    if (filters.start_date) queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.of_model_id !== undefined)
      queryParams.append('of_model_id', filters.of_model_id?.toString() || '');
    if (filters.company_id !== undefined)
      queryParams.append('company_id', filters.company_id?.toString() || '');

    // Choose endpoint based on grouping
    let endpoint = '/client/transactions/stats/by_days';
    if (filters.grouping === 'week') {
      endpoint = '/client/transactions/stats/by_weeks';
    } else if (filters.grouping === 'month') {
      endpoint = '/client/transactions/stats/by_month';
    }

    return ApiClient.get(`${endpoint}?${queryParams.toString()}`);
  },

  getCurrentMonthStats: () => ApiClient.get('/client/get_current_month_stats'),
};

export default clientStatisticService;
export type { StatisticsFilters, StatisticsResponse };
